import { EmailProviderFactory } from './EmailProviderFactory';
import type { EmailData, RegistrationNotificationData, EmailResponse } from './types';
import { authLogger } from '../../utils/logger';

/**
 * Clean Email Service using Factory Pattern
 * 
 * This service provides a simple interface for sending emails
 * while abstracting away the complexity of different providers.
 */
export class EmailService {
  private static instance: EmailService;
  private factory: EmailProviderFactory;

  private constructor() {
    this.factory = EmailProviderFactory.getInstance();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  /**
   * Send a contact form message
   */
  async sendMessage(data: EmailData): Promise<EmailResponse> {
    try {
      // Add timestamp if not provided
      const emailData: EmailData = {
        ...data,
        timestamp: data.timestamp || new Date().toISOString()
      };

      authLogger.info('📧 EmailService: Sending contact message', {
        to: emailData.to,
        userEmail: emailData.userEmail,
        userName: emailData.userName,
        context: emailData.context
      });

      // Get the appropriate provider and send email
      const provider = this.factory.getProvider();
      const result = await provider.sendEmail(emailData);

      if (result.success) {
        authLogger.info('✅ EmailService: Contact message sent successfully', {
          provider: provider.getProviderName(),
          messageId: result.messageId,
          to: emailData.to
        });
      } else {
        authLogger.error('❌ EmailService: Contact message failed', {
          provider: provider.getProviderName(),
          error: result.message,
          to: emailData.to
        });
      }

      return result;

    } catch (error: any) {
      authLogger.error('❌ EmailService: Unexpected error sending contact message', {
        error: error.message || error,
        to: data.to
      });

      return {
        success: false,
        message: `Failed to send email: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Send a registration notification
   */
  async sendRegistrationNotification(data: RegistrationNotificationData): Promise<EmailResponse> {
    try {
      authLogger.info('📧 EmailService: Sending registration notification', {
        userEmail: data.userEmail,
        userName: data.userName,
        tenantName: data.tenantName,
        subscriptionTier: data.subscriptionTier
      });

      // Get the appropriate provider and send email
      const provider = this.factory.getProvider();
      const result = await provider.sendRegistrationNotification(data);

      if (result.success) {
        authLogger.info('✅ EmailService: Registration notification sent successfully', {
          provider: provider.getProviderName(),
          messageId: result.messageId,
          userEmail: data.userEmail
        });
      } else {
        authLogger.error('❌ EmailService: Registration notification failed', {
          provider: provider.getProviderName(),
          error: result.message,
          userEmail: data.userEmail
        });
      }

      return result;

    } catch (error: any) {
      authLogger.error('❌ EmailService: Unexpected error sending registration notification', {
        error: error.message || error,
        userEmail: data.userEmail
      });

      return {
        success: false,
        message: `Failed to send registration notification: ${error.message || 'Unknown error'}`
      };
    }
  }

  /**
   * Get current provider information
   */
  getProviderInfo(): { name: string; configured: boolean } {
    try {
      const provider = this.factory.getProvider();
      return {
        name: provider.getProviderName(),
        configured: provider.validateConfiguration()
      };
    } catch (error) {
      return {
        name: 'Unknown',
        configured: false
      };
    }
  }

  /**
   * Get status of all providers
   */
  getProvidersStatus() {
    return this.factory.getProviderStatus();
  }

  /**
   * Test current provider
   */
  async testCurrentProvider(): Promise<EmailResponse> {
    const testData: EmailData = {
      to: import.meta.env.VITE_SEND_TO_EMAIL || 'info@fabrik28.ai',
      subject: 'Email Service Test',
      message: 'This is a test email to verify the email service is working correctly.',
      userEmail: 'test@fabrik28.ai',
      userName: 'Email Service Test',
      timestamp: new Date().toISOString(),
      context: 'test'
    };

    return this.sendMessage(testData);
  }

  /**
   * Test all available providers
   */
  async testAllProviders() {
    return this.factory.testAllProviders();
  }
}

// Export singleton instance
export const emailService = EmailService.getInstance();

// Export types for external use
export type { EmailData, RegistrationNotificationData, EmailResponse } from './types';
